import _noop from 'lodash/noop';
import _map from 'lodash/map';
import { ValidationError } from 'yup';
import { ProductsProvider } from 'types/graphql';
import { getLogger } from 'utils/datadog-logger';

import { getDataSharingEnabledForUser } from 'utils/fides/helpers';

import { getPersistedValue } from 'shared/utils/persisted-values';

import SHARED_ROUTES from 'shared/routes';
import { createTelemetryClient } from '../../clients';
import { EventTracker, AnalyticsContext } from '../../events';

import { Data, eventsSchema, validateSchema, placedOrderCartItemSchema, cartItemSchema } from './schemas';
import {
  getCommonProps,
  getItemProps,
  getCartItemProps,
  getCartTotalValue,
  getOrderProps,
  getPlacedOrderCartItemProps,
  getRoundUpTheChangeAnalyticsDataProps,
} from './utils';

const loggingEnabled = !!getPersistedValue('analytics:telemetryTracker');
const logger = getLogger('telemetry-tracker');

function checkTelemetryEnabledForEvent(context: AnalyticsContext, event: string): boolean {
  const { disabledEvents = [] } = context.featureFlags[`growth.ecomm.telemetry.event-kill-switch.operational`] ?? {};

  if (event === 'onContextChange') {
    return !disabledEvents.includes('viewedPage');
  }

  return !disabledEvents.includes(event);
}

// TODO: we should move this to Armageddon, I beleive this is an implementation detail for Moloco consumer
export function removeQueryParams(path = ''): string {
  if (!path) {
    return '';
  }

  try {
    // If it's a full URL, parse it and get the pathname
    if (path.includes('://') || path.includes('www.') || /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.exec(path)) {
      const url = new URL(path.startsWith('http') ? path : `https://${path}`);
      return url.pathname;
    }
  } catch {
    // If URL parsing fails, fall through to original logic
  }

  const index = path.indexOf('?');
  return index !== -1 ? path.slice(0, index) : path;
}

export function decorate(eventTracker: EventTracker): EventTracker {
  const decorated = { ...eventTracker };

  Object.entries(eventTracker).forEach(([event, handler]) => {
    if (!(event in eventsSchema || event === 'onContextChange')) {
      return;
    }

    decorated[event] = (payload: any, context: AnalyticsContext, oldContext: AnalyticsContext) => {
      const clientLoggingEnabled = !context.featureFlags['ecomm.telemetry.client-logging.kill-switch'];

      if (!checkTelemetryEnabledForEvent(context, event)) {
        if (clientLoggingEnabled)
          logger.info('telemetry.client.event', { event_type: event, status: 'blocked_kill_switch' });
        return;
      }

      if (!getDataSharingEnabledForUser()) {
        if (clientLoggingEnabled)
          logger.info('telemetry.client.event', { event_type: event, status: 'blocked_opt_out' });
        return;
      }

      try {
        handler(payload, context, oldContext);
      } catch (err) {
        if (err instanceof ValidationError) {
          if (clientLoggingEnabled)
            logger.info('telemetry.client.event', { event_type: event, status: 'blocked_validation_error' });
          if (loggingEnabled) {
            console.error(err);
          }
        } else {
          throw err;
        }
      }
    };
  });

  return decorated;
}

function getPageType(normalizedPath: string | undefined): string | undefined {
  switch (normalizedPath) {
    case SHARED_ROUTES.DISPENSARY_CNAME:
    case SHARED_ROUTES.EMBEDDED_MENU_CNAME:
    case SHARED_ROUTES.MOBILE_ECOMM_CNAME:
    case SHARED_ROUTES.KIOSKS_CNAME:
      return 'home';
    default:
      return undefined;
  }
}

export const createTelemetryTracker = (): EventTracker => {
  const client = createTelemetryClient();

  return decorate({
    onContextChange: (_updates, newContext, oldContext) => {
      const { activePath } = newContext;

      const activeBasePath = removeQueryParams(activePath?.concrete);
      const oldBasePath = removeQueryParams(oldContext.activePath?.concrete);

      if (activeBasePath !== oldBasePath) {
        const pageType = getPageType(activePath?.normalized);
        const data: Data = {
          type: 'viewedPage',
          payload: {
            ...getCommonProps(newContext),
            // TODO: we should always pass page type
            ...(pageType && { pageType }),
          },
        };

        validateSchema(data);
        void client.post(data);
      }
    },

    cartViewed: (_payload, context) => {
      const data: Data = {
        type: 'cartViewed',
        payload: getCommonProps(context),
      };

      validateSchema(data);
      void client.post(data);
    },

    dutchiePayEnrollmentButtonClicked: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayEnrollmentButtonClicked',
        payload: {
          ...getCommonProps(context),
          button_location: payload.buttonLocation,
        },
      };
      validateSchema(data);
      void client.post(data);
    },

    dutchiePayEnrollmentStep: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayEnrollmentStep',
        payload: {
          ...getCommonProps(context),
          step: payload.step,
          action: payload.action,
          enrollment_source: payload.enrollmentSource,
          context: payload.context,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayEnrollmentStepCancelled: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayEnrollmentStepCancelled',
        payload: {
          ...getCommonProps(context),
          step: payload.step,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayPlaidOnEvent: (payload, context) => {
      const invalidPlaidEvents = ['TRANSITION_VIEW'];
      if (invalidPlaidEvents.includes(payload.eventName)) {
        return;
      }

      const data: Data = {
        type: 'dutchiePayPlaidOnEvent',
        payload: {
          ...getCommonProps(context),
          connection_method: payload.connectionMethod,
          event_name: payload.eventName,
          error_code: payload.eventMetadata.error_code,
          error_message: payload.eventMetadata.error_message,
          institution_id: payload.eventMetadata.institution_id,
          institution_name: payload.eventMetadata.institution_name,
          mfa_type: payload.eventMetadata.mfa_type,
          selection: payload.eventMetadata.selection,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayPlaidOnLoad: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayPlaidOnLoad',
        payload: {
          ...getCommonProps(context),
          connection_method: payload.connectionMethod,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayPlaidOnSuccess: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayPlaidOnSuccess',
        payload: {
          ...getCommonProps(context),
          institution_id: payload.eventMetadata.institution?.institution_id ?? null,
          institution_name: payload.eventMetadata.institution?.name ?? null,
          connection_method: payload.connectionMethod,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayPlaidOnExit: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayPlaidOnExit',
        payload: {
          ...getCommonProps(context),
          exit_point: payload.eventMetadata.status,
          error_code: payload.plaidError?.error_code ?? null,
          error_message: payload.plaidError?.error_message ?? null,
          institution_id: payload.eventMetadata.institution?.institution_id ?? null,
          institution_name: payload.eventMetadata.institution?.name ?? null,
          connection_method: payload.connectionMethod,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayAeroSyncOnEvent: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayAeroSyncOnEvent',
        payload: {
          ...getCommonProps(context),
          event_name: payload.eventName,
          widget_title: payload.eventMetadata.pageTitle,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayAeroSyncOnLoad: (_payload, context) => {
      const data: Data = {
        type: 'dutchiePayAeroSyncOnLoad',
        payload: {
          ...getCommonProps(context),
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayAeroSyncOnClose: (_payload, context) => {
      const data: Data = {
        type: 'dutchiePayAeroSyncOnClose',
        payload: {
          ...getCommonProps(context),
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayAeroSyncOnSuccess: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayAeroSyncOnSuccess',
        payload: {
          ...getCommonProps(context),
          connection_id: payload.connectionId,
          client_name: payload.clientName,
          aero_pass_user_uuid: payload.aeroPassUserUuid,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayAeroSyncOnError: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayAeroSyncOnError',
        payload: {
          ...getCommonProps(context),
          error: payload.error,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayPreAuthEvent: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayPreAuthEvent',
        payload: {
          ...getCommonProps(context),
          event_name: payload.eventName,
          error_type: payload.eventMetadata?.error?.type ?? null,
          error_message: payload.eventMetadata?.error?.message ?? null,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
          checkout_token: context.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayCTAButtonClicked: (_payload, context) => {
      const data: Data = {
        type: 'dutchiePayCTAButtonClicked',
        payload: {
          ...getCommonProps(context),
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayRelinkButtonViewed: (_payload, context) => {
      const data: Data = {
        type: 'dutchiePayRelinkButtonViewed',
        payload: {
          ...getCommonProps(context),
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayRelinkButtonClicked: (_payload, context) => {
      const data: Data = {
        type: 'dutchiePayRelinkButtonClicked',
        payload: {
          ...getCommonProps(context),
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    // Barcode View
    dutchiePayInstoreAccountLinkViewed: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayInstoreAccountLinkViewed',
        payload: {
          ...getCommonProps(context),
          show_barcode: payload.showBarcode,
        },
      };
      validateSchema(data);
      void client.post(data);
    },

    dutchiePayInstoreCartViewed: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayInstoreCartViewed',
        payload: {
          ...getCommonProps(context),
          instore_cart_id: payload.instoreCartId,
          checkout_token: context.checkoutToken,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
        },
      };
      validateSchema(data);
      void client.post(data);
    },

    dutchiePayInstoreCartApprovalClicked: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayInstoreCartApprovalClicked',
        payload: {
          ...getCommonProps(context),
          instore_cart_id: payload.instoreCartId,
          checkout_token: context.checkoutToken,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayInstoreCartApprovalSucceeded: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayInstoreCartApprovalSucceeded',
        payload: {
          ...getCommonProps(context),
          instore_cart_id: payload.instoreCartId,
          checkout_token: context.checkoutToken,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    dutchiePayInstoreCartApprovalFailed: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayInstoreCartApprovalFailed',
        payload: {
          ...getCommonProps(context),
          instore_cart_id: payload.instoreCartId,
          checkout_token: context.checkoutToken,
          is_kiosk_order: context.isKioskOrder ?? false,
          is_hub_order: context.isHubOrder ?? false,
        },
      };
      validateSchema(data);
      void client.post(data);
    },

    dutchiePayKioskQRCodeModalViewed: (payload, context) => {
      const data: Data = {
        type: 'dutchiePayKioskQRCodeModalViewed',
        payload: {
          ...getCommonProps(context),
          checkout_token: payload.checkoutToken,
        },
      };
      validateSchema(data);
      void client.post(data);
    },

    viewedKioskCheckoutModal: (_payload, context) => {
      const items = _map(context.activeOrder?.cart, getCartItemProps).filter((item: unknown) =>
        cartItemSchema.isValidSync(item)
      );

      if (items.length === 0) {
        return;
      }

      const data: Data = {
        type: 'viewedKioskCheckoutModal',
        payload: {
          ...getCommonProps(context),
          items,
          ...(context.activeCart?.subtotal != null && { subtotal: context.activeCart.subtotal }),
        },
      };
      validateSchema(data);
      void client.post(data);
    },

    addedProductToCart: (payload, context) => {
      const data: Data = {
        type: 'addedProductToCart',
        payload: {
          ...getCommonProps(context),
          item: getCartItemProps(payload),
        },
      };

      validateSchema(data);
      void client.post(data);
    },

    removedProductFromCart: (payload, context) => {
      const data: Data = {
        type: 'removedProductFromCart',
        payload: {
          ...getCommonProps(context),
          item: getCartItemProps(payload),
        },
      };

      validateSchema(data);
      void client.post(data);
    },

    placedOrder: (payload, context) => {
      // exclude invalid items
      const items = _map(context.activeOrder?.cart, getPlacedOrderCartItemProps).filter((item: unknown) =>
        placedOrderCartItemSchema.isValidSync(item)
      );

      const commonProps = getCommonProps(context);
      const orderProps = getOrderProps(payload.order);

      const data: Data = {
        type: 'placedOrder',
        payload: {
          ...commonProps,
          items,
          total: getCartTotalValue(context),
          ...orderProps,
          ...getRoundUpTheChangeAnalyticsDataProps(payload.roundupForCharityAnalyticsData),
        },
      };

      validateSchema(data);
      void client.post(data, { resetSessionOnSuccess: commonProps.menu_variant === 'kiosk' });
    },

    searchResults: (payload, context) => {
      const data: Data = {
        type: 'searchResults',
        payload: {
          ...getCommonProps(context),
          query: payload.query,
        },
      };

      validateSchema(data);
      void client.post(data);
    },

    viewedProduct: (payload, context) => {
      const data: Data = {
        type: 'viewedProduct',
        payload: {
          ...getCommonProps(context),
          item: getItemProps(payload),
        },
      };

      validateSchema(data);
      void client.post(data);
    },

    sponsoredBannerImpression: (payload, context) => {
      const { products, impressionUrl } = payload;

      const data: Data = {
        type: 'sponsoredBannerImpression',
        payload: {
          ...getCommonProps(context),
          tracking_url: impressionUrl,
          items: products.map((item) => getItemProps({ product: item })),
        },
      };

      validateSchema(data);
      void client.post(data);
    },

    sponsoredBannerClicked: (payload, context) => {
      const { products, clickUrl } = payload;

      const data: Data = {
        type: 'sponsoredBannerClicked',
        payload: {
          ...getCommonProps(context),
          tracking_url: clickUrl,
          items: products.map((item) => getItemProps({ product: item })),
        },
      };

      validateSchema(data);
      void client.post(data);
    },

    sponsoredProductImpression: (payload, context) => {
      const { product, position } = payload;

      const data: Data = {
        type: 'sponsoredProductImpression',
        payload: {
          ...getCommonProps(context),
          tracking_url: product.adTrackers?.impressionUrl,
          item: getItemProps({ product }),
          position,
        },
      };

      validateSchema(data);
      void client.post(data);
    },

    sponsoredProductClicked: (payload, context) => {
      const { product, position } = payload;

      const data: Data = {
        type: 'sponsoredProductClicked',
        payload: {
          ...getCommonProps(context),
          tracking_url: product.adTrackers?.clickUrl,
          item: getItemProps({ product }),
          position,
        },
      };

      validateSchema(data);
      void client.post(data);
    },

    productClicked: (payload, context) => {
      const { product } = payload;

      let productProvider = ProductsProvider.dutchie;

      if ('provider' in product && product.provider) {
        productProvider = product.provider;
      }

      const data: Data = {
        type: 'productClicked',
        payload: {
          ...getCommonProps(context),
          placement: payload.placement,
          position: payload.position,
          provider: productProvider,
          item: getItemProps({ product }),
        },
      };

      validateSchema(data);
      void client.post(data);
    },
    roundupForCharityViewed: (_payload, context) => {
      const data: Data = {
        type: 'roundupForCharityViewed',
        payload: {
          ...getCommonProps(context),
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    roundupForCharityToggled: (payload, context) => {
      const data: Data = {
        type: 'roundupForCharityToggled',
        payload: {
          ...getCommonProps(context),
          action: payload.action,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    loginNudge: (payload, context) => {
      const { action } = payload;

      const data: Data = {
        type: 'loginNudge',
        payload: {
          ...getCommonProps(context),
          action,
        },
      };

      validateSchema(data);
      void client.post(data);
    },
    walletPass: (payload, context) => {
      const { action, type } = payload;

      const data: Data = {
        type: 'walletPass',
        payload: {
          ...getCommonProps(context),
          action,
          type,
        },
      };

      validateSchema(data);
      void client.post(data);
    },
    // Instore Enrollment events
    instoreEnrollmentStepViewed: (payload, context) => {
      const data: Data = {
        type: 'instoreEnrollmentStepViewed',
        payload: {
          ...getCommonProps(context),
          step: payload.step,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    instoreEnrollmentStepCompleted: (payload, context) => {
      const data: Data = {
        type: 'instoreEnrollmentStepCompleted',
        payload: {
          ...getCommonProps(context),
          step: payload.step,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    instoreEnrollmentStepSkipped: (payload, context) => {
      const data: Data = {
        type: 'instoreEnrollmentStepSkipped',
        payload: {
          ...getCommonProps(context),
          step: payload.step,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    medCardSignupCompleted: (payload, context) => {
      const data: Data = {
        type: 'medCardSignupCompleted',
        payload: {
          ...getCommonProps(context),
          waiting_at_dispensary: payload.waitingAtDispensary,
        },
      };
      validateSchema(data);
      void client.post(data);
    },
    medCardSignupFailed: (payload, context) => {
      const data: Data = {
        type: 'medCardSignupFailed',
        payload: {
          ...getCommonProps(context),
          error_type: payload.errorType,
          error_message: payload.errorMessage,
        },
      };
      validateSchema(data);
      void client.post(data);
    },

    // Noop events
    cartUpdated: _noop,
    accountLogin: _noop,
    buyItAgainImpression: _noop,
    buyItAgainViewAllClicked: _noop,
    checkoutStarted: _noop,
    checkoutSession: _noop,
    searchedForProduct: _noop,
    imageBannerClicked: _noop,
    paymentsError: _noop,
    privacyPreferenceUpdated: _noop,
    dutchiePayInstoreLoginViewed: _noop,
    loyaltySignup: _noop,
    gaGTMClickEvent: _noop,
    dispensaryClicked: _noop,
    viewedCheckout: _noop,
  });
};
